/**
 * Storage Utilities
 * 
 * Centralized storage key generation and cleanup utilities
 * Includes user/org isolation for multi-tenant support
 */

import AuthService from '../../../Service/auth.service';
import { Organization } from '../../../Entities/Organization';
import { JobPostMode } from '../types';

const STORAGE_PREFIX = 'jobPostV3_draft';

/**
 * Generate a short unique draft ID (alphanumeric, no underscores, URL-safe).
 * Used as the storage key suffix for new/clone drafts so each session gets
 * its own slot and doesn't overwrite other in-progress drafts.
 */
export const generateDraftId = (): string => {
  return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
};

/**
 * Generate storage key with user/org isolation.
 * Format: jobPostV3_draft_{userId}_{orgId}_{mode}_{draftId}
 *
 * draftId should be:
 *   - edit mode  → the jobPostId (one edit session per job post)
 *   - new mode   → a UUID from generateDraftId() (unique per draft session)
 *   - clone mode → a UUID from generateDraftId() (unique per clone session)
 */
export const generateStorageKey = (
  mode: 'new' | 'edit' | 'clone',
  draftId: string,
  userId?: string,
  orgId?: string
): string => {
  let user = userId;
  let org = orgId;

  if (!user || !org) {
    const currentUser = AuthService.getInstance().getCurrentUser();
    user = user || currentUser?._id || 'anonymous';
    org = org || currentUser?.orgId || 'no-org';
  }

  return `${STORAGE_PREFIX}_${user}_${org}_${mode}_${draftId}`;
};

/**
 * Parse storage key to extract components.
 * Returns draftId as the last segment (may be a UUID, jobPostId, or legacy 'new').
 */
export const parseStorageKey = (key: string): {
  userId: string;
  orgId: string;
  mode: JobPostMode;
  draftId: string;
} | null => {
  if (!key.startsWith(STORAGE_PREFIX)) {
    return null;
  }

  // Format: jobPostV3_draft_{userId}_{orgId}_{mode}_{draftId}
  const parts = key.replace(`${STORAGE_PREFIX}_`, '').split('_');

  if (parts.length < 4) {
    return null;
  }

  const userId = parts[0];
  const orgId = parts[1];
  const mode = parts[2] as JobPostMode;
  // draftId may contain underscores (e.g. legacy jobPostId), so rejoin the rest
  const draftId = parts.slice(3).join('_');

  return { userId, orgId, mode, draftId };
};

/**
 * Get current user and org IDs
 */
export const getCurrentUserAndOrg = (): { userId?: string; orgId?: string } => {
  const currentUser = AuthService.getInstance().getCurrentUser();
  return {
    userId: currentUser?._id,
    orgId: currentUser?.orgId,
  };
};

/**
 * Clear all drafts for a specific user and org
 */
export const clearUserDrafts = (userId?: string, orgId?: string): number => {
  const { userId: currentUserId, orgId: currentOrgId } = getCurrentUserAndOrg();
  const targetUserId = userId || currentUserId;
  const targetOrgId = orgId || currentOrgId;
  
  if (!targetUserId || !targetOrgId) {
    return 0;
  }
  
  const prefix = `${STORAGE_PREFIX}_${targetUserId}_${targetOrgId}_`;
  let clearedCount = 0;
  
  try {
    // Iterate backwards to avoid index issues when removing items
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    }
  } catch (error) {
    console.error('Error clearing user drafts:', error);
  }
  
  return clearedCount;
};

/**
 * Clear all drafts (for admin/debug purposes)
 */
export const clearAllDrafts = (): number => {
  let clearedCount = 0;
  
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    }
  } catch (error) {
    console.error('Error clearing all drafts:', error);
  }
  
  return clearedCount;
};

/**
 * Clear old drafts (older than specified days) for current user
 */
export const clearOldDrafts = (daysOld: number = 7, userId?: string, orgId?: string): number => {
  const { userId: currentUserId, orgId: currentOrgId } = getCurrentUserAndOrg();
  const targetUserId = userId || currentUserId;
  const targetOrgId = orgId || currentOrgId;
  
  if (!targetUserId || !targetOrgId) {
    return 0;
  }
  
  const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
  const prefix = `${STORAGE_PREFIX}_${targetUserId}_${targetOrgId}_`;
  let clearedCount = 0;
  
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const item = localStorage.getItem(key);
        if (item) {
          try {
            const parsed = JSON.parse(item);
            if (parsed.timestamp && parsed.timestamp < cutoffTime) {
              localStorage.removeItem(key);
              clearedCount++;
            }
          } catch {
            // Invalid draft, remove it
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      }
    }
  } catch (error) {
    console.error('Error clearing old drafts:', error);
  }
  
  return clearedCount;
};

/**
 * Get storage prefix for filtering
 */
export const getStoragePrefix = (): string => {
  return STORAGE_PREFIX;
};

